import { IDropdownOption } from '@fluentui/react';
import {
  Button,
  buttonStylesXSmall,
  Checkbox,
  ClassNamesFromIStyles,
  ConfirmDialog,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import React, { useEffect, useState } from 'react';

import {
  dialogContentStyles,
  IMultiSelectStyles,
  multiSelectOptionStyles,
  multiselectPrimaryButtonStyles,
  multiSelectSearchStyles,
  multiSelectStylesDefault,
} from './MultiSelect.styles';

interface IMultiSelectProps {
  label?: string;
  searchPlaceholder?: string;
  placeholder?: string;
  options: IDropdownOption[];
  initialSelectedOptionKeys?: string[];
  onChange: (selected: string[]) => void;
}

const MultiSelectDialog: React.FC<IMultiSelectProps> = ({
  options,
  onChange,
  label,
  initialSelectedOptionKeys,
  searchPlaceholder,
  placeholder,
}) => {
  const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialSelectedOptionKeys || []);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    setSelectedOptions(initialSelectedOptionKeys || []);
  }, [initialSelectedOptionKeys]);

  const toggleDialog = (): void => {
    setTempSelectedOptions(selectedOptions);
    setIsDialogOpen(!isDialogOpen);
    setSearchTerm('');
  };

  const handleSelect = (option: string): void => {
    setTempSelectedOptions((prev) => {
      const newSelection = prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option];
      return newSelection;
    });
  };

  const selectAll = (): void => setTempSelectedOptions(options.map((option) => option.key as string));
  const deselectAll = (): void => setTempSelectedOptions([]);

  const handleConfirm = (): void => {
    setSelectedOptions(tempSelectedOptions);
    onChange(tempSelectedOptions);
    setIsDialogOpen(false);
  };

  const filteredOptions = options.filter((option) => option.text.toLowerCase().includes(searchTerm.toLowerCase()));

  const classNames = useClassNames<IMultiSelectStyles, ClassNamesFromIStyles<IMultiSelectStyles>>(
    'multi-select-component',
    multiSelectStylesDefault
  );

  return (
    <div className={classNames.root}>
      {label && <label className={classNames.label}>{label}</label>}
      <Button
        disabled={options.length <= 0}
        onClick={toggleDialog}
        styles={multiselectPrimaryButtonStyles}
        menuIconName="ChevronDown"
      >
        {selectedOptions.length ? `${selectedOptions.length} Selected` : placeholder}
      </Button>
      <ConfirmDialog
        hidden={!isDialogOpen}
        onDismiss={toggleDialog}
        onConfirm={handleConfirm}
        dialogContentProps={{
          title: 'Select Options',
          styles: dialogContentStyles,
        }}
        content={
          <div className={classNames.contentRoot}>
            <Search
              emptyMessage="No results found"
              placeholder={searchPlaceholder}
              onSearchTextChange={(text: string) => setSearchTerm(text)}
              styles={multiSelectSearchStyles()}
            />
            <div className={classNames.selectButtonContainer}>
              <Button styles={buttonStylesXSmall} onClick={deselectAll}>
                Deselect All
              </Button>
              <Button styles={buttonStylesXSmall} onClick={selectAll}>
                Select All
              </Button>
            </div>
            <div className={classNames.optionsContainer}>
              {filteredOptions.map((option) => (
                <Checkbox
                  key={option.key}
                  label={option.text}
                  checked={tempSelectedOptions.includes(option.key as string)}
                  onChange={() => handleSelect(option.key as string)}
                  styles={multiSelectOptionStyles}
                />
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default MultiSelectDialog;
